import StraightenIcon from "@mui/icons-material/Straighten";
import { Stack, Tooltip, Typography } from "@mui/material";

import { ShipmentDetail } from "components/Shipment/components/ShipmentDetail";
import { useDisclosure } from "hooks";
import {
  isRouteCreatedAfterNavigationRollout,
  meterToKilometer,
  meterToMile,
  removeDecimalPoint,
} from "utils";

import { DeleteSuggestedRouteModal } from "./DeleteSuggestedRouteModal";
import { SuggestedNavigationRouteMap } from "../SuggestedNavigationRouteMap";

export interface SuggestedRouteDeleteContext {
  shipmentId: number;
  shipmentType: ShipmentType;
  onDeleted: () => void;
}

interface Props {
  route: Route;
  origin: RoutePoint;
  destination: RoutePoint;
  midPoints: RoutePoint[];
  deleteContext?: SuggestedRouteDeleteContext;
}

export function SuggestedRoute(props: Props) {
  const { route, origin, destination, midPoints, deleteContext } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { navigation: suggestedRoute } = route;

  if (suggestedRoute?.payload == null) {
    return (
      <div>
        <Typography
          sx={{ mt: "1rem", mb: "1rem" }}
          textAlign="center"
          variant="body1"
        >
          {isRouteCreatedAfterNavigationRollout(route?.created_at)
            ? "Suggested route not available yet, please check back later."
            : "Suggested route not available for this shipment."}
        </Typography>
      </div>
    );
  }

  const getSuggestedNavigationRouteMapOrPlaceholder = () => {
    if (
      origin?.point_location.latitude != null &&
      origin.point_location.longitude != null &&
      destination?.point_location.latitude != null &&
      destination.point_location.longitude != null &&
      midPoints.map(
        (mp) =>
          mp.point_location.latitude != null &&
          mp.point_location.longitude != null,
      ).length === midPoints.length
    ) {
      return (
        <SuggestedNavigationRouteMap
          navigation={suggestedRoute}
          origin={{
            longitude: origin.point_location.longitude,
            latitude: origin.point_location.latitude,
            point_number: origin.point_number,
          }}
          destination={{
            longitude: destination.point_location.longitude,
            latitude: destination.point_location.latitude,
            point_number: destination.point_number,
          }}
          midPoints={midPoints.map((mp) => ({
            longitude: mp.point_location.longitude as number,
            latitude: mp.point_location.latitude as number,
            point_number: mp.point_number,
          }))}
        />
      );
    }

    return (
      <Typography textAlign="center">
        Suggested route map not avilable. Please check back later
      </Typography>
    );
  };

  return (
    <Stack direction="column" width="100%">
      <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
        <ShipmentDetail
          icon={<StraightenIcon />}
          title="Route Distance"
          text={`${removeDecimalPoint(meterToMile(suggestedRoute.distance_meters))} mi
           (${removeDecimalPoint(meterToKilometer(suggestedRoute.distance_meters))} kms)`}
        />
      </Stack>
      {getSuggestedNavigationRouteMapOrPlaceholder()}
      <Typography variant="body1" textAlign="center" sx={{ mt: ".5rem" }}>
        Route distance is approximate and may vary. Please verify as needed.
      </Typography>
      {deleteContext != null && (
        <Stack direction="column" alignItems="center" sx={{ mt: "1rem" }}>
          <Tooltip title="Hide the map and route distance if the route shown looks wrong">
            <Typography
              variant="body2"
              color="text.secondary"
              onClick={onOpen}
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Route on Map Incorrect?
            </Typography>
          </Tooltip>
          <DeleteSuggestedRouteModal
            isOpen={isOpen}
            onClose={onClose}
            shipmentId={deleteContext.shipmentId}
            shipmentType={deleteContext.shipmentType}
            onDeleted={deleteContext.onDeleted}
          />
        </Stack>
      )}
    </Stack>
  );
}
